/**
 * The unified "Add user" door — one modal for every person who did NOT
 * come from the OneTouch sync.
 *
 * Ed, 2026-09-17: *"when i go to create ed@hagerty.net, I need to be able
 * to point that user at multiple environments - i want ed@hagerty.net to
 * be able to get to training and demo for example, or production and
 * training."*
 *
 * So the door grants a SET, never a single environment: one row per
 * environment, a role chosen inside each. That is ADR-0045's grid applied
 * at the moment of creation, and it is why there is no "Environment"
 * dropdown here — a dropdown can only ever express one answer.
 *
 * ☠️ PRODUCTION AND A TENANT ENVIRONMENT ARE DIFFERENT ACCOUNT POOLS.
 * Granting both does not produce one credential that roams. Production
 * grants a role to a login that already exists in the main pool;
 * training/demo/develop each MINT a separate account inside that
 * environment's own Identity Platform tenant. The person ends up holding
 * two sign-ins with two different passwords. The form says this out loud
 * whenever both kinds are selected, because otherwise the correct result
 * reads as a bug.
 *
 * ☠️ The mechanism is the SERVER's to declare (`EnvironmentSpec.mechanism`)
 * and is never inferred from the environment's name here. Two exist:
 *
 *   "invite"  training, demo, develop. `training_invite_action` mints an
 *             account in that environment's own tenant and the person
 *             signs in with an emailed link — no password is ever created.
 *   "role"    production. `practitioner_role_action` provisions the
 *             person record if it is missing (idempotent) and then
 *             assigns the role. NOTHING is minted: the main pool has no
 *             account-creating path and the production surface is
 *             password-only, so someone with no login is refused with an
 *             honest message rather than left half-made.
 */
import {
  callEnvironmentInviteAction,
  callPractitionerRoleAction,
  type AccessRoleValue,
  type EnvironmentSpec,
} from "./cf-client";
import { sendPasswordResetEmail } from "firebase/auth";

import { auth } from "../firebase";
import { escapeHtml } from "../utils/html";
import { roleLabel, surfaceFor } from "./access-labels";
import { formRules, grantAll, type Outcome, type Pick } from "./add-user-rules";

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultExpiry(): string {
  return new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Open the door. `onDone` re-renders the caller once a user has actually
 * been added — the modal stays open on failure so the entered details
 * aren't lost to a typo'd address, and stays open on SUCCESS too, because
 * with several environments the per-environment summary is the receipt.
 */
export function openAddUserModal(onDone: () => void): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box edit-role-modal add-user-modal" role="dialog" aria-labelledby="addUserTitle">
      <h2 id="addUserTitle">Add user</h2>
      <p class="edit-role-subject">For anyone who didn't come from the OneTouch
        sync — a colleague, a consultant, a specialist working with us. Tick
        every environment they need, and choose their role in each.</p>
      <div class="edit-role-form">
        <label class="edit-role-field">
          <span class="edit-role-label">Email</span>
          <input type="email" id="addUserEmail" autocomplete="off" spellcheck="false"
                 placeholder="consultant@firm.co.uk" />
        </label>
        <label class="edit-role-field">
          <span class="edit-role-label">Given name</span>
          <input type="text" id="addUserGiven" autocomplete="off" placeholder="Alex" />
        </label>
        <label class="edit-role-field" id="addUserFamilyField">
          <span class="edit-role-label">Family name</span>
          <input type="text" id="addUserFamily" autocomplete="off" />
        </label>
        <div class="edit-role-field">
          <span class="edit-role-label">Environments</span>
          <div class="add-user-env-list" id="addUserEnvList" role="group"
               aria-labelledby="addUserTitle">Loading…</div>
          <p class="add-user-env-note" id="addUserEnvNote" hidden></p>
        </div>
        <label class="edit-role-field" id="addUserExpiresField">
          <span class="edit-role-label">Access until</span>
          <input type="date" id="addUserExpires" value="${defaultExpiry()}" min="${today()}" />
        </label>
      </div>
      <div class="edit-role-status" id="addUserStatus" aria-live="polite"></div>
      <div class="modal-actions">
        <button class="btn-outline" id="addUserCancel">Close</button>
        <button class="btn-primary" id="addUserSave" disabled>Create access</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("#addUserCancel")?.addEventListener("click", close);

  const el = <T extends HTMLElement>(id: string) => overlay.querySelector<T>(`#${id}`)!;
  const status = el<HTMLElement>("addUserStatus");
  const saveBtn = el<HTMLButtonElement>("addUserSave");
  const envList = el<HTMLElement>("addUserEnvList");
  const envNote = el<HTMLElement>("addUserEnvNote");
  const familyField = el<HTMLElement>("addUserFamilyField");
  const familyInput = el<HTMLInputElement>("addUserFamily");
  const expiresField = el<HTMLElement>("addUserExpiresField");
  const expiresInput = el<HTMLInputElement>("addUserExpires");

  // ☠️ The door NO LONGER carries the guest list (Ed, 2026-09-18). It lived
  // here from 2026-09-16, when Users & Roles could only show production role
  // grants and this was the only place non-production access was visible.
  // Since 1.4.51 the list itself shows every person and every environment,
  // and since 1.4.52 their row edits it — so the copy here was a second,
  // older-looking way to do the same job, with its own Extend/Revoke buttons
  // and its own "env: training" labels. One door adds someone; the list is
  // where access is seen and changed.

  let environments: EnvironmentSpec[] = [];

  const setStatus = (text: string, isError = false) => {
    status.textContent = text;
    status.classList.toggle("edit-role-status-error", isError);
  };

  /**
   * Render the per-environment receipt. Called after EVERY environment,
   * not once at the end: a set of grants is partial by nature, and an
   * admin who sees only the last line cannot tell which half landed.
   */
  const setOutcomes = (outcomes: Outcome[]) => {
    status.innerHTML = outcomes
      .map(
        (o) =>
          `<div class="add-user-outcome${o.ok ? "" : " add-user-outcome-bad"}">` +
          `<strong>${escapeHtml(o.environment)}</strong> — ${escapeHtml(o.message)}</div>`,
      )
      .join("");
    status.classList.toggle(
      "edit-role-status-error",
      outcomes.some((o) => !o.ok),
    );
  };

  const rowFor = (environment: string) =>
    envList.querySelector<HTMLInputElement>(`input[data-env="${CSS.escape(environment)}"]`);

  const roleFor = (environment: string) =>
    envList.querySelector<HTMLSelectElement>(`select[data-env="${CSS.escape(environment)}"]`);

  /** Every ticked environment, in the order the SERVER listed them. */
  const selections = (): Pick[] =>
    environments
      .filter((spec) => rowFor(spec.environment)?.checked)
      .map((spec) => ({ spec, role: roleFor(spec.environment)?.value ?? "" }));

  /**
   * The form follows the SELECTION; the rules live in add-user-rules.ts,
   * where they are tested. One expiry date covers every guest grant: a
   * guest invited to two environments on two different end dates is a case
   * nobody has asked for, and a per-row control would cost more than it
   * buys. The role, by contrast, lives on the row, because the roles on
   * offer are whatever THAT environment grants.
   */
  const syncForm = () => {
    const picks = selections();
    const rules = formRules(picks);

    // A row's role is meaningless until its environment is ticked, and an
    // enabled control that does nothing is a lie about what will happen.
    for (const spec of environments) {
      const select = roleFor(spec.environment);
      if (select) select.disabled = !rowFor(spec.environment)?.checked;
    }

    familyField.hidden = !rules.needsFamily;
    familyInput.required = rules.needsFamily;
    expiresField.hidden = !rules.needsExpiry;
    expiresInput.required = rules.needsExpiry;

    envNote.hidden = !rules.twoPools;
    if (rules.twoPools) {
      envNote.textContent =
        "Production and the other environments are separate account pools. " +
        "This person will end up with TWO sign-ins: their production login, " +
        "and a new account minted in each other environment ticked here.";
    }

    saveBtn.disabled = !rules.canSave;
  };

  // Load the environment list before enabling the door: offering a
  // hardcoded fallback would be exactly the drift ADR-0045 removed.
  void (async () => {
    try {
      const envs = await callEnvironmentInviteAction({
        action: "list-environments",
        // Required by the envelope; ignored for this action, which is the
        // one question that cannot presuppose a provisioned environment.
        environment: "training",
      });
      environments = Array.isArray(envs) ? (envs as EnvironmentSpec[]) : [];
      if (environments.length === 0) {
        envList.textContent = "No environments are available to grant.";
        setStatus("No environments are available to grant.", true);
        return;
      }
      envList.innerHTML = environments
        .map((e) => {
          const env = escapeHtml(e.environment);
          const roles = e.roles
            .map((r) => `<option value="${escapeHtml(r)}">${escapeHtml(roleLabel(r))}</option>`)
            .join("");
          return `
            <label class="add-user-env-row">
              <input type="checkbox" class="add-user-env-check" data-env="${env}" />
              <span class="add-user-env-name">${env}</span>
              <select class="add-user-env-role" data-env="${env}" disabled
                      aria-label="Role in ${env}">${roles}</select>
            </label>`;
        })
        .join("");
      envList.addEventListener("change", syncForm);
      syncForm();
    } catch (err) {
      envList.textContent = "Couldn't load environments.";
      setStatus(err instanceof Error ? err.message : "Couldn't load environments.", true);
    }
  })();

  /**
   * The production path: provision the record, then grant the role. Both
   * calls are idempotent, so the common case (an existing colleague) costs
   * one wasted round trip rather than a "does this person exist?" probe.
   *
   * Returns its message rather than writing the status itself — with a set
   * of environments there is no single status line to own.
   */
  const grantProduction = async (
    email: string,
    given: string,
    family: string,
    role: string,
    spec: EnvironmentSpec,
  ): Promise<string> => {
    await callPractitionerRoleAction({
      action: "provision-person",
      email,
      given,
      family,
      note: "created via the Add user door",
    });
    await callPractitionerRoleAction({
      action: "assign",
      email,
      role: role as AccessRoleValue,
      // The door grants real scope; a test-scoped production role is a
      // narrower case, changed from the person's row afterwards.
      dataScope: "real",
    });
    return `${roleLabel(role)} granted. They sign in at ${surfaceFor(spec, role)}.`;
  };

  /**
   * Establish access in ONE environment. Never throws: a set of grants
   * must keep going after a failure, or one bad environment silently
   * cancels the others the admin asked for.
   */
  const grantOne = async (
    pick: Pick,
    person: { email: string; given: string; family: string; expires: string },
  ): Promise<Outcome> => {
    const { spec, role } = pick;
    const { email, given, family, expires } = person;
    try {
      if (spec.mechanism === "role") {
        return {
          environment: spec.environment,
          ok: true,
          message: await grantProduction(email, given, family, role, spec),
        };
      }
      const openEnded = role !== "family";
      await callEnvironmentInviteAction({
        action: "invite",
        environment: spec.environment,
        role,
        email,
        // A staff grant carries no expiry at all — an empty string would
        // be a malformed date, not "open-ended".
        ...(openEnded ? {} : { expires }),
        ...(given ? { given } : {}),
      });
      return {
        environment: spec.environment,
        ok: true,
        message:
          `Invited as ${roleLabel(role)}. Send them ONLY ${surfaceFor(spec, role)} — ` +
          `they choose "Email me a sign-in link" there. ` +
          (openEnded ? "Access is open-ended." : `Access ends ${expires}.`),
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't add that user.";

      // The new-starter recovery. Production can grant a role, but the
      // person may have no login yet — so ask, once, in plain words, and
      // create one only on a yes.
      if (/No Firebase Auth user/i.test(message) && spec.mechanism === "role") {
        // ☠️ The second prompt. Minting a production credential is the
        // most consequential thing this door does, and it happens only
        // after somebody reads this sentence and agrees to it.
        const agreed = window.confirm(
          `${email} has no SweetTree login.\n\n` +
            `Create a production login for them now, and email them a link to ` +
            `set their own password?\n\n` +
            `No password is created here or shown to anyone — they choose it ` +
            `from the email.`,
        );
        if (!agreed) {
          return {
            environment: spec.environment,
            ok: false,
            message: "No login created — nothing was changed here.",
          };
        }
        try {
          await callPractitionerRoleAction({
            action: "create-login",
            email,
            displayName: `${given} ${family}`.trim(),
          });
          // Firebase sends its OWN branded template — this project's
          // reset-password email is already customised — so there is no
          // sending path of ours to maintain, and no deliverability
          // surface we have to own.
          await sendPasswordResetEmail(auth, email);
          const granted = await grantProduction(email, given, family, role, spec);
          return {
            environment: spec.environment,
            ok: true,
            message: `Login created and a password link emailed. ${granted}`,
          };
        } catch (recoveryErr) {
          return {
            environment: spec.environment,
            ok: false,
            message:
              recoveryErr instanceof Error
                ? recoveryErr.message
                : "Couldn't create the login.",
          };
        }
      }

      return { environment: spec.environment, ok: false, message };
    }
  };

  saveBtn.addEventListener("click", () => {
    void (async () => {
      const value = (id: string) => el<HTMLInputElement>(id).value.trim();
      const email = value("addUserEmail");
      const given = value("addUserGiven");
      const family = value("addUserFamily");
      const expires = expiresInput.value;
      const picks = selections();

      if (picks.length === 0) {
        setStatus("Tick at least one environment.", true);
        return;
      }
      if (!email || !given) {
        setStatus("Email and given name are both required.", true);
        return;
      }
      if (picks.some((p) => p.spec.mechanism === "role") && !family) {
        setStatus("A family name is required to create a person record.", true);
        return;
      }

      saveBtn.disabled = true;
      setStatus(
        `Setting up ${picks.length} environment${picks.length === 1 ? "" : "s"}…`,
      );

      // Sequential, reported after every environment — see grantAll.
      const outcomes = await grantAll(
        picks,
        (pick) => grantOne(pick, { email, given, family, expires }),
        setOutcomes,
      );

      // One refresh of the caller however many environments landed: the
      // Users & Roles list is where the result shows up now.
      if (outcomes.some((o) => o.ok)) onDone();
      // Re-enabled either way: a partial result is the case where the
      // admin most needs to correct one environment and try again.
      saveBtn.disabled = false;
    })();
  });
}
